import { useContext } from 'react';
import { Alert } from 'src/components/ui';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { FinancialXlsxUploadResult, OcifXlsxGlobalError, OcifXlsxRowError } from '../../../api/OcifXlsxApi';
import UWEFormContext from '../../../context/UWEFormContext';
import { AccordionItem } from '../../ui/Accordion';
import Icon from 'src/components/Icon';

export function useScheduleTitle() {
  const uweFormContext = useContext(UWEFormContext);
  const subForms = uweFormContext?.subForms ?? {};
  return (name: string): string => {
    const key = Object.keys(subForms).find(k => k.toLowerCase() === name.toLowerCase());
    return key ? subForms[key].title ?? name : name;
  };
}

export function GlobalErrorPanel({ errors }: { errors: OcifXlsxGlobalError[] }) {
  if (!errors?.length) return null;
  return (
    <Alert color="danger" className="p-3 mb-3" fade={false}>
      <strong className="block mb-2">
        <Icon icon="XCircle" className="mr-1" />
        <Trans>Upload Error</Trans>
      </strong>
      {errors.map((e, i) => (
        <div key={i} className="small">
          <span className="font-bold mr-1">{e.code}:</span>
          {e.message}
        </div>
      ))}
    </Alert>
  );
}

export function ScheduleErrorGrid({ schedule, errors }: { schedule: string; errors: OcifXlsxRowError[] }) {
  const MAX_SHOWN = 10;
  const shown = errors.slice(0, MAX_SHOWN);
  const overflow = errors.length - shown.length;

  return (
    <AccordionItem
      defaultOpen
      className="mb-3"
      color="warning"
      title={
        <div className="flex gap-2 items-center">
          <strong>{schedule}</strong>
          <span className="badge bg-danger">{errors.length}</span>
        </div>
      }
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '2.5rem 9rem 1fr',
          columnGap: '0.75rem',
          rowGap: '0.25rem',
          fontSize: '0.78rem',
          padding: '0.4rem 0.75rem',
          paddingBottom: '1rem',
        }}
      >
        <span className="text-muted" style={{ fontSize: '0.68rem', textTransform: 'uppercase' }}>
          <Trans>Row</Trans>
        </span>
        <span className="text-muted" style={{ fontSize: '0.68rem', textTransform: 'uppercase' }}>
          <Trans>Field</Trans>
        </span>
        <span className="text-muted" style={{ fontSize: '0.68rem', textTransform: 'uppercase' }}>
          <Trans>Issue</Trans>
        </span>
        {shown.map((e, i) => (
          <div key={i} style={{ display: 'contents' }}>
            <span>{e.row}</span>
            <code style={{ fontSize: '0.78rem' }}>{e.fieldCode}</code>
            <span>{e.error}</span>
          </div>
        ))}
        {overflow > 0 && (
          <span className="text-muted italic" style={{ gridColumn: '1 / -1' }}>
            +{overflow} <Trans>more</Trans>
          </span>
        )}
      </div>
    </AccordionItem>
  );
}

export function IgnoredSchedulesNotice({ result }: { result: FinancialXlsxUploadResult }) {
  const getTitle = useScheduleTitle();

  const ignored = Object.entries(result?.scheduleResults ?? {})
    .filter(([, sr]) => sr.status === 'ignored' && sr.ignoredReason === 'not-in-reporting-window')
    .map(([name]) => ({ name, title: getTitle(name) }));

  if (!ignored.length) return null;

  return (
    <Alert color="secondary" className="p-3 mb-3 mt-2" fade={false}>
      <strong className="block mb-2">
        <Icon icon="Prohibit" className="mr-1" />
        <Trans>Ignored — Not in Required Schedules</Trans>
      </strong>
      <p className="small mb-2">
        <Trans>
          The following tabs were present in the uploaded file but are not part of the required schedules for this reporting window. No data was
          stored for them.
        </Trans>
      </p>
      <ul className="mb-0 small pl-3">
        {ignored.map(({ name, title }) => (
          <li key={name}>{title}</li>
        ))}
      </ul>
    </Alert>
  );
}

export function FinancialNoticePanel({ result }: { result: FinancialXlsxUploadResult }) {
  const getTitle = useScheduleTitle();

  if (!result || result.status === 'error') return null;

  const isPartial = result.status === 'partial';
  const isCleanPartial =
    isPartial && (result.errorRows ?? 0) === 0 && (result.globalErrors?.length ?? 0) === 0 && (result.rowErrors?.length ?? 0) === 0;

  const alertColor = isPartial && !isCleanPartial ? 'warning' : 'success';
  const heading = isPartial && !isCleanPartial ? t`Uploaded with Notices` : t`Upload Successful`;
  const icon = isPartial && !isCleanPartial ? 'exclamation-triangle' : 'check-circle';
  const schedulesWithErrors = Object.entries(result.scheduleResults ?? {}).filter(([, sr]) => sr.errors?.length > 0);

  return (
    <Alert color={alertColor} className="p-3 mb-3 mt-4" fade={false}>
      <div className="flex justify-between items-center mb-2">
        <strong>
          <Icon icon={`fa fa-${icon}`} className="mr-1" />
          {heading}
        </strong>
        <small className="text-muted">
          {result.fileName} · {result.totalRows} <Trans>rows read</Trans> · {result.validRows} <Trans>stored</Trans> · {result.errorRows}{' '}
          <Trans>ignored</Trans>
        </small>
      </div>
      {isCleanPartial && (
        <div className="small mt-1" style={{ opacity: 0.85 }}>
          <Icon icon="Info" className="mr-1" />
          <Trans>Notice: There were no errors or issues in this upload, but not all fields were included in the upload file.</Trans>
        </div>
      )}
      {schedulesWithErrors.length > 0 && (
        <div className="mt-3">
          {schedulesWithErrors.map(([schedule, sr]) => (
            <ScheduleErrorGrid key={schedule} schedule={getTitle(schedule)} errors={sr.errors} />
          ))}
        </div>
      )}
    </Alert>
  );
}
